/**
 * Builds a comparator that reports whether two arrays carry the same ids in
 * the same positions, reading each item's id with `idOf`. Values that are not
 * both arrays compare by reference.
 *
 * Reselect keeps its previous result when `resultEqualityCheck` reports a
 * match, so a selector that rebuilds an equal list hands back the array it
 * returned last time. Consumers memoizing on that array then skip work they
 * would otherwise redo on every workspace state change. Comparing ids rather
 * than the items themselves means a refetch producing equivalent records with
 * fresh object identities still counts as no change.
 *
 * Reselect also hands this comparator the results of a selector's *input*
 * selectors, when its development-mode stability check runs them twice to see
 * whether they return something new each call. Those results are whole state
 * objects as often as they are lists, and for them the only question the check
 * asks is whether the two runs produced the same value, which is what the
 * reference comparison answers.
 */
export function sameIdsInOrder<T>(
  idOf: (item: T) => string,
): (a: unknown, b: unknown) => boolean {
  return function haveSameIds(a: unknown, b: unknown): boolean {
    if (!Array.isArray(a) || !Array.isArray(b)) {
      return a === b;
    }
    return (
      a.length === b.length &&
      a.every((item: T, index) => idOf(item) === idOf(b[index] as T))
    );
  };
}
